import type { Role } from '@econdominio/core/nav';
import type { ReactNode } from 'react';
import { normalizePath } from './router';
import { ActionCards } from './screens/ActionCards';
import { BalanceteDoMes } from './screens/BalanceteDoMes';
import { Balancetes } from './screens/Balancetes';
import { Conferencia } from './screens/Conferencia';
import { Documentos } from './screens/Documentos';
import { FaturaDestaque } from './screens/FaturaDestaque';
import { Faturas } from './screens/Faturas';
import { FaturasDoMes } from './screens/FaturasDoMes';
import { Fechamento } from './screens/Fechamento';
import { Fundos } from './screens/Fundos';
import { Gas } from './screens/Gas';
import { Home } from './screens/Home';
import { Indicadores } from './screens/Indicadores';
import { Perguntas } from './screens/Perguntas';
import { Pessoas } from './screens/Pessoas';
import { Previsao } from './screens/Previsao';
import { Privacidade } from './screens/Privacidade';
import { Recebimentos } from './screens/Recebimentos';
import { RelatorioDoMes } from './screens/RelatorioDoMes';
import { TrocaSenha } from './screens/TrocaSenha';
import { Unidades } from './screens/Unidades';
import { Usuarios } from './screens/Usuarios';
import { Vinculos } from './screens/Vinculos';

/**
 * Tabela de rotas do app do tenant: path → tela → papéis → módulo. Os papéis e o
 * módulo espelham a autorização da API (`requireRole`/`contabilReadOnly` e o gating
 * de módulos, #126) para que nenhum menu ou card leve a uma tela que responderia
 * 403. O menu inferior vem de `navForRole` (`@econdominio/core/nav`) e precisa
 * apontar para paths desta tabela.
 *
 * `/privacidade` e `/cadastro` são rotas públicas tratadas antes da sessão (App.tsx).
 */

export type TenantRole = Exclude<Role, 'super_admin'>;

export const TENANT_ROLES: readonly TenantRole[] = [
  'morador',
  'proprietario',
  'sindico',
  'contabil',
];

/** Telas que agrupam outras no menu: Financeiro (síndico), Relatórios (contábil), conta. */
export type HubId = 'financeiro' | 'relatorios' | 'conta';

export const HUBS: readonly HubId[] = ['financeiro', 'relatorios', 'conta'];

export interface RouteCard {
  readonly icon: string;
  readonly title: string;
  readonly desc: string;
}

/** Quem está acessando: papel e módulos ativos do tenant (de `/api/me`). */
export interface Acesso {
  readonly papel: string;
  readonly modulos: ReadonlySet<string>;
  /** Condomínio suspenso (#127): telas abrem em modo leitura. */
  readonly suspenso: boolean;
}

export interface RouteDef {
  readonly id: string;
  readonly path: string;
  /** Título do cabeçalho da página; vazio quando a própria tela traz o título. */
  readonly title: string;
  readonly roles: readonly TenantRole[];
  /** Módulo opcional da tela (mesmo código do gating da API); ausente = core. */
  readonly modulo?: string;
  /** Presente quando a tela aparece como card na Home e nos hubs. */
  readonly card?: RouteCard;
  readonly hubs?: readonly HubId[];
  readonly render: (acesso: Acesso) => ReactNode;
}

const TODOS = TENANT_ROLES;
const OPERACAO: readonly TenantRole[] = ['sindico', 'contabil'];

const ROUTES: readonly RouteDef[] = [
  {
    id: 'home',
    path: '/',
    title: '',
    roles: TODOS,
    // Síndico: checklist do fechamento do mês acima dos atalhos (docs/08 §8.2).
    // Morador e proprietário: a fatura mais recente com "Baixar fatura" (#140).
    render: (a) => (
      <Home
        cards={cardsFor(a)}
        topo={
          a.papel === 'sindico' ? (
            <Fechamento editavel={!a.suspenso} />
          ) : a.papel === 'morador' || a.papel === 'proprietario' ? (
            <FaturaDestaque />
          ) : undefined
        }
      />
    ),
  },
  {
    // Sem card: abre pelo "Ver quem falta" do checklist. GET é de síndico e contábil.
    id: 'recebimentos',
    path: '/recebimentos',
    title: 'Quem falta pagar',
    roles: OPERACAO,
    render: () => <Recebimentos />,
  },
  {
    id: 'faturas',
    path: '/faturas',
    title: 'Minhas faturas',
    roles: ['morador', 'proprietario'],
    card: { icon: '🧾', title: 'Minhas faturas', desc: 'Histórico, detalhe e Pix.' },
    render: () => <Faturas />,
  },
  {
    id: 'balancetes',
    path: '/balancetes',
    title: 'Balancetes publicados',
    roles: TODOS,
    modulo: 'balancete',
    card: { icon: '📊', title: 'Balancetes', desc: 'Prestação de contas publicada.' },
    hubs: ['financeiro', 'relatorios'],
    render: () => <Balancetes />,
  },
  {
    id: 'previsao',
    path: '/previsao',
    title: 'Previsão orçamentária',
    roles: OPERACAO,
    card: { icon: '📅', title: 'Previsão', desc: 'Orçamento da competência.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; POST/PATCH/DELETE só do síndico, e suspenso é somente leitura.
    render: (a) => <Previsao editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'gas',
    path: '/gas',
    title: 'Gás do mês',
    roles: OPERACAO,
    modulo: 'gas',
    card: { icon: '🔥', title: 'Gás', desc: 'Leituras, foto do medidor e rateio.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; escritas só do síndico, e suspenso é somente leitura.
    render: (a) => <Gas editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'faturas-do-mes',
    path: '/faturas-do-mes',
    title: 'Faturas do mês',
    roles: OPERACAO,
    card: { icon: '🧾', title: 'Faturas do mês', desc: 'Gerar, avisar e registrar pagamentos.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; gerar, avisar e pagar só do síndico, e suspenso é somente leitura.
    render: (a) => <FaturasDoMes editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'fundos',
    path: '/fundos',
    title: 'Posição dos fundos',
    roles: OPERACAO,
    card: { icon: '🏦', title: 'Fundos', desc: 'Posição no início e no fim do mês.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil na tela; POST só do síndico, e suspenso é somente leitura.
    render: (a) => <Fundos editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'documentos',
    path: '/documentos',
    title: 'Documentos',
    roles: OPERACAO,
    card: { icon: '📁', title: 'Documentos', desc: 'Comprovantes, títulos e posições.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; envio só do síndico, e suspenso é somente leitura.
    render: (a) => <Documentos editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'balancete-do-mes',
    path: '/balancete-do-mes',
    title: 'Balancete do mês',
    roles: OPERACAO,
    modulo: 'balancete',
    card: { icon: '📑', title: 'Balancete do mês', desc: 'Rascunho, envio do PDF e publicação.' },
    hubs: ['financeiro', 'relatorios'],
    // Rascunho e versões: síndico e contábil; enviar e publicar só do síndico, e suspenso é somente leitura.
    render: (a) => <BalanceteDoMes editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'relatorio-do-mes',
    path: '/relatorio-do-mes',
    title: 'Relatório do mês',
    roles: OPERACAO,
    modulo: 'relatorios',
    card: { icon: '🧮', title: 'Relatório do mês', desc: 'CSV, fechamento do extrato e rascunho.' },
    hubs: ['financeiro', 'relatorios'],
    // Só leitura: export.csv e rascunho são de síndico e contábil; banco e balancete só se ligados.
    render: (a) => (
      <RelatorioDoMes banco={a.modulos.has('banco')} balancete={a.modulos.has('balancete')} />
    ),
  },
  {
    id: 'conferencia',
    path: '/conferencia',
    title: 'Conferência de extrato',
    roles: OPERACAO,
    modulo: 'banco',
    card: { icon: '✅', title: 'Conferência', desc: 'Conciliação do extrato.' },
    hubs: ['financeiro', 'relatorios'],
    // Só o síndico de condomínio ativo edita: o PATCH é do síndico e suspenso é somente leitura.
    render: (a) => <Conferencia editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'unidades',
    path: '/unidades',
    title: 'Unidades',
    roles: OPERACAO,
    card: { icon: '🏢', title: 'Unidades', desc: 'Unidades e fração ideal do rateio.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; POST/PATCH só do síndico, e suspenso é somente leitura.
    render: (a) => <Unidades editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'pessoas',
    path: '/pessoas',
    title: 'Pessoas',
    roles: OPERACAO,
    card: { icon: '👥', title: 'Pessoas', desc: 'Proprietários, moradores e contatos.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; POST/PATCH só do síndico, e suspenso é somente leitura.
    render: (a) => <Pessoas editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'vinculos',
    path: '/vinculos',
    title: 'Vínculos',
    roles: OPERACAO,
    card: { icon: '🔗', title: 'Vínculos', desc: 'Quem é proprietário, inquilino ou pagante.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; POST/DELETE só do síndico, e suspenso é somente leitura.
    render: (a) => <Vinculos editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    id: 'usuarios',
    path: '/usuarios',
    title: 'Usuários',
    roles: OPERACAO,
    card: { icon: '🪪', title: 'Usuários', desc: 'Convites, acessos e redefinição de senha.' },
    hubs: ['financeiro', 'relatorios'],
    // GET é de síndico e contábil; POST/PATCH/reset só do síndico, e suspenso é somente leitura.
    render: (a) => <Usuarios editavel={a.papel === 'sindico' && !a.suspenso} />,
  },
  {
    // Contábil fica de fora: perguntar é POST e o contábil é somente-leitura.
    id: 'convencao',
    path: '/convencao',
    title: '',
    roles: ['morador', 'proprietario', 'sindico'],
    modulo: 'ia_convencao',
    card: { icon: '💬', title: 'Convenção', desc: 'Perguntas com citação.' },
    render: (a) => <Perguntas somenteLeitura={a.suspenso} />,
  },
  {
    id: 'senha',
    path: '/senha',
    title: 'Trocar senha',
    roles: TODOS,
    card: { icon: '🔑', title: 'Trocar senha', desc: 'Atualize sua senha.' },
    hubs: ['conta'],
    render: () => <TrocaSenha />,
  },
  {
    id: 'meus-dados',
    path: '/meus-dados',
    title: 'Privacidade e meus dados',
    roles: TODOS,
    card: { icon: '🔒', title: 'Privacidade', desc: 'Seus dados, consentimento e solicitações.' },
    hubs: ['conta'],
    render: () => <Privacidade />,
  },
  {
    id: 'indicadores',
    path: '/indicadores',
    title: 'Indicadores históricos',
    roles: ['sindico', 'contabil', 'proprietario'],
    modulo: 'relatorios',
    card: {
      icon: '📈',
      title: 'Indicadores',
      desc: 'Séries históricas: saldo, inadimplência, fundos, gás.',
    },
    hubs: ['financeiro', 'relatorios'],
    render: () => <Indicadores />,
  },
  {
    id: 'financeiro',
    path: '/financeiro',
    title: 'Financeiro',
    roles: ['sindico'],
    render: (a) => <ActionCards routes={cardsFor(a, 'financeiro')} />,
  },
  {
    id: 'relatorios',
    path: '/relatorios',
    title: 'Relatórios',
    roles: ['contabil'],
    render: (a) => <ActionCards routes={cardsFor(a, 'relatorios')} />,
  },
  {
    id: 'perfil',
    path: '/perfil',
    title: 'Perfil',
    roles: ['morador', 'proprietario', 'contabil'],
    render: (a) => <ActionCards routes={cardsFor(a, 'conta')} />,
  },
  {
    id: 'ajustes',
    path: '/ajustes',
    title: 'Ajustes',
    roles: ['sindico'],
    render: (a) => <ActionCards routes={cardsFor(a, 'conta')} />,
  },
];

/** Papel autorizado e módulo da tela ligado para o tenant. */
function permitida(route: RouteDef, a: Acesso): boolean {
  const papelOk = (route.roles as readonly string[]).includes(a.papel);
  return papelOk && (route.modulo === undefined || a.modulos.has(route.modulo));
}

/** Telas com card permitidas ao acesso: todas (Home) ou só as do hub informado. */
export function cardsFor(a: Acesso, hub?: HubId): readonly RouteDef[] {
  return ROUTES.filter(
    (r) => r.card && permitida(r, a) && (hub === undefined || r.hubs?.includes(hub)),
  );
}

export type RouteResolution =
  | { kind: 'ok'; route: RouteDef }
  | { kind: 'forbidden' }
  | { kind: 'not_found' };

/**
 * Resolve o path para a tela do acesso. O Início abre para qualquer usuário
 * autenticado (destino de todo redirecionamento, não pode virar loop).
 */
export function resolveRoute(pathname: string, a: Acesso): RouteResolution {
  const path = normalizePath(pathname);
  const route = ROUTES.find((r) => r.path === path);
  if (!route) return { kind: 'not_found' };
  if (route.id === 'home' || permitida(route, a)) return { kind: 'ok', route };
  return { kind: 'forbidden' };
}
